import {Injectable} from '@angular/core';
import {FilterControlPrepService} from "./filter-control-prep.service";
import {FilterResultService} from "./filter-result.service";
import {FilterControls} from "../interface/filter-controls";
import {FilterProductPreview} from "../interface/filter-product-preview";
import {arrayDeepCopy} from "bmx-pastebox";
import {FilterKey} from "../interface/filter-key";
import {FilterControl} from "../interface/filter-control";
import {FilterRangeOption} from "../interface/filter-range-option";
import {FilterOption} from "../interface/filter-option";
import {FilterControlOption} from "../interface/filter-control-option";

@Injectable({
  providedIn: 'root'
})
export class FilterService {
	constructor(
		private _prepService: FilterControlPrepService,
		private _result: FilterResultService
	) {
	}

	public selectedFilterControls: FilterControlOption[] = [];


	private _defaultOrSubFilter(
		preview: FilterProductPreview,
		filterKey: FilterKey,
		filterValues: string[]
	): boolean {

		if (!filterKey.subKey) {
			return false;
		}
		return filterValues.includes(preview[filterKey.subKey] as string);
	}

	private _csvFilter(
		preview: FilterProductPreview,
		filterKey: FilterKey,
		filterValues: string[]
	): boolean {
		const csvValues: Set<string> = new Set(preview[filterKey.key].split(','));
		return filterValues.some(value => csvValues.has(value));

	}

	private _rangeFilter(
		preview: FilterProductPreview,
		filterKey: FilterKey,
		rangeCohort: FilterRangeOption): boolean
	{
		return preview[filterKey.key] >= rangeCohort.value1 && preview[filterKey.key] <= rangeCohort.value2;
	}

	public prepareFilterControls(
		controls: FilterControls,
		previews: FilterProductPreview[]
	): FilterControls {

		controls.keys.forEach(key => controls.cohorts.push(
			this._prepService.prepareFilterControlFromKey(
				key,
				previews
			)
		));

		return controls;
	}

	public prepareResult(
		previews: FilterProductPreview[],
		controls: FilterControls
	): FilterProductPreview[] {

		let result: FilterProductPreview[] = arrayDeepCopy(previews);
		this.selectedFilterControls = [];

		controls.cohorts.forEach(cohort => {
			const filterKey: FilterKey = cohort.key;

			if(cohort.rangeCohort ) {
				if(
					cohort.rangeCohort.value1 > cohort.rangeCohort.defaultMin ||
					cohort.rangeCohort.value2 < cohort.rangeCohort.defaultMax
				) {
					this.selectedFilterControls.push({
						name: cohort.title,
						type: "range",
						range: cohort.rangeCohort,
					});
					result = result.filter((preview: FilterProductPreview) => {
						return this._rangeFilter(
							preview,
							filterKey,
							cohort.rangeCohort? cohort.rangeCohort : {} as FilterRangeOption
						);
					});
				}
				else return;
			}

			if (!cohort.cohort) return;
			const filterValues: string[] = this._result.getFilterValuesFromKey(
				filterKey,
				cohort.cohort.options
			);
			const activeFilterControls: FilterControlOption[] = this._result.getFilterControlsFromKey(filterKey, cohort);
			if (activeFilterControls && activeFilterControls.length > 0) {
				this.selectedFilterControls.push(...activeFilterControls);
			}
			if (filterValues.length > 0) {
				result = result.filter((preview: FilterProductPreview) => {

					if (filterKey.type === 'csv') {
						return this._csvFilter(
							preview,
							filterKey,
							filterValues
						);
					}
					else if (filterKey.type === "toggle") {
						if(filterKey.key === "inStock" && cohort.cohort!.options[0].active)
							return preview.total_quantity > 0 ||
								preview.made_to_order_fabric_quantity > 0 ||
								(preview.product_size_profile_option_list && preview.product_size_profile_option_list[0].quantity > 0);
						else
							return false;
					}
					return this._defaultOrSubFilter(
						preview,
						filterKey,
						filterValues
					);
				});
			}

			if(filterKey.type === "toggle" && filterKey.key === "inStock") {
				if(cohort.cohort.options[0].active)
					result = result.filter((preview: FilterProductPreview) =>
						preview.total_quantity > 0 ||
						preview.made_to_order_fabric_quantity > 0 ||
						(preview.product_size_profile_option_list && preview.product_size_profile_option_list[0].quantity > 0)
					);
			}
		});

		return result;
	}

	public clearAllFilter(controls: FilterControls): void {
		controls.cohorts.forEach((control: FilterControl): void => {
			if (control.key.type === "csv" || control.key.type === "default" || control.key.type === "toggle") {
				if(!control.cohort)
					return;
				control.cohort.options.forEach(option => {
					if(!['accessories', 'home', 'apparel', 'swatchkit'].includes(option.value.toLowerCase()))
						option.active = false;
				});
			}
			else if (control.key.type === "sub") {
				if(!control.cohort)
					return;
				control.cohort.options.forEach((option: FilterOption): void => {
					option.active = false;
					if (!option.subOptions)
						return;
					option.subOptions.forEach(subOption => subOption.active = false);
				})
			}
			else if (control.key.type === "range") {
				if(!control.rangeCohort)
					return;
				control.rangeCohort.active = false;
				control.rangeCohort.value1 = control.rangeCohort.defaultMin;
				control.rangeCohort.value2 = control.rangeCohort.defaultMax;
			}
		});
	}
}
